/**
 * Action Icons
 * 
 * Icons representing user actions like edit, delete, copy, publish, etc.
 * These icons typically appear in buttons, menus, and toolbars.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { getIconClasses } from '../iconUtils';

const iconPropTypes = {
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']),
  color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'warning', 'danger', 'neutral', 'white']),
  className: PropTypes.string,
};

/**
 * EditIcon - Pencil in square for editing actions
 */
export const EditIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

EditIcon.propTypes = iconPropTypes;

/**
 * PencilIcon - Simple pencil icon for inline editing
 */
export const PencilIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

PencilIcon.propTypes = iconPropTypes;

/**
 * TrashIcon - Trash bin for delete actions
 */
export const TrashIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

TrashIcon.propTypes = iconPropTypes;

/**
 * CopyIcon - Duplicate/copy icon
 */
export const CopyIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

CopyIcon.propTypes = iconPropTypes;

/**
 * PublishIcon - Cloud upload for publishing
 */
export const PublishIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

PublishIcon.propTypes = iconPropTypes;

/**
 * DraftIcon - Cloud with checkmark for draft status
 */
export const DraftIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 12l2 2 4-4" />
  </svg>
);

DraftIcon.propTypes = iconPropTypes;

/**
 * AnalyticsIcon - Bar chart for analytics
 */
export const AnalyticsIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

AnalyticsIcon.propTypes = iconPropTypes;

/**
 * ExternalLinkIcon - External link arrow
 */
export const ExternalLinkIcon = ({ size = 'md', color = 'inherit', className = '', inline = false, ...props }) => (
  <svg 
    className={`${getIconClasses(inline ? 'xs' : size, color, className)} ${inline ? 'ml-1 inline-block' : ''} transition-transform duration-150 group-hover:scale-110`}
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

ExternalLinkIcon.propTypes = {
  ...iconPropTypes,
  inline: PropTypes.bool,
};

/**
 * ContinueIcon - External link with plus for "continue creating"
 */
export const ContinueIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    {...props}
  >
    <rect x="0" fill="none" width="24" height="24"></rect>
    <g>
      <path d="M21 14v5c0 1.105-.895 2-2 2H5c-1.105 0-2-.895-2-2V5c0-1.105.895-2 2-2h5v2H5v14h14v-5h2z"></path>
      <path d="M21 7h-4V3h-2v4h-4v2h4v4h2V9h4"></path>
    </g>
  </svg>
);

ContinueIcon.propTypes = iconPropTypes;

/**
 * SortOrderIcon - Clock with chevron for sort by date
 * Uses 'inherit' to get color from parent, allowing theme-aware styling
 */
export const SortOrderIcon = ({ size = 'md', color = 'inherit', className = '', order = 'desc', active = false, ...props }) => (
  <svg 
    className={`${getIconClasses(size, 'inherit', className)}`}
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor"
    {...props}
  >
    <circle cx="12" cy="12" r="8" strokeWidth="2" fill="none"/>
    <path d="M12 12h3" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 12V9" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="12" r="1" fill="currentColor"/>
    {active && (
      order === "desc"
        ? <path d="M21 2.5 L22.6 4.1 L23.8 2.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        : <path d="M21 4.5 L22.6 2.9 L23.8 4.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    )}
  </svg>
);

SortOrderIcon.propTypes = {
  ...iconPropTypes,
  order: PropTypes.oneOf(['asc', 'desc']),
  active: PropTypes.bool,
};

/**
 * SortAlphaIcon - A-Z with chevron for alphabetical sort
 * Uses 'inherit' to get color from parent, allowing theme-aware styling
 */
export const SortAlphaIcon = ({ size = 'md', color = 'inherit', className = '', order = 'asc', active = false, ...props }) => (
  <svg 
    className={`${getIconClasses(size, 'inherit', className)}`}
    viewBox="0 0 24 24" 
    fill="none"
    {...props}
  >
    <text x="12" y="16" textAnchor="middle" fontSize="12" fontWeight="700" fill="currentColor" fontFamily="system-ui, -apple-system, sans-serif">A-Z</text>
    {active && (
      order === "desc"
        ? <path d="M21 2.5 L22.6 4.1 L23.8 2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        : <path d="M21 4.5 L22.6 2.9 L23.8 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    )}
  </svg>
);

SortAlphaIcon.propTypes = {
  ...iconPropTypes,
  order: PropTypes.oneOf(['asc', 'desc']),
  active: PropTypes.bool,
};

/**
 * PlusIcon - Plus/add icon
 */
export const PlusIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
  </svg>
);

PlusIcon.propTypes = iconPropTypes;

/**
 * MinusIcon - Minus/subtract icon
 */
export const MinusIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14" />
  </svg>
);

MinusIcon.propTypes = iconPropTypes;

/**
 * PaletteIcon - Simple color palette / swatch icon (used for color picking actions)
 */
export const PaletteIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    {/* Outer palette */}
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 3a9 9 0 100 18h1.2a2.3 2.3 0 002.2-2.9l-.3-1a2 2 0 011.9-2.6H18a3 3 0 003-3 8.5 8.5 0 00-9-8.5z"
    />
    {/* Dots */}
    <path strokeWidth="2" strokeLinecap="round" d="M8.2 10.2h.01" />
    <path strokeWidth="2" strokeLinecap="round" d="M10.9 7.9h.01" />
    <path strokeWidth="2" strokeLinecap="round" d="M13.9 8.8h.01" />
    <path strokeWidth="2" strokeLinecap="round" d="M15.8 11.5h.01" />
  </svg>
);

PaletteIcon.propTypes = iconPropTypes;

/**
 * FontSizeUpIcon - "A" with upward arrow (increase font size)
 */
export const FontSizeUpIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <path d="M12.8101986,4.41064097 L12.8776346,4.51935775 L12.930792,4.63445088 L18.430792,18.6390155 C18.6326793,19.1530777 18.3796114,19.7334694 17.8655491,19.9353566 C17.3882056,20.1228233 16.8536698,19.9180073 16.6181008,19.4762044 L16.569208,19.3701137 L15.245,15.999 L8.754,15.999 L7.43079205,19.3701137 C7.24332536,19.8474572 6.72949509,20.0997597 6.24546021,19.9717253 L6.13445088,19.9353566 C5.65710736,19.74789 5.4048049,19.2340597 5.53283932,18.7500248 L5.56920795,18.6390155 L11.069208,4.63445088 C11.3689581,3.8712017 12.3728054,3.7965984 12.8101986,4.41064097 Z M12,7.734 L9.539,13.999 L14.46,13.999 L12,7.734 Z M17.6834824,3.07042232 C17.8845216,2.97641824 18.1174942,2.97641824 18.3185334,3.07042232 L18.4160963,3.12525804 L20.6671042,4.62105637 L20.7549114,4.68916802 C21.0264848,4.93394581 21.0850834,5.3471779 20.876677,5.66080596 C20.6682707,5.97443402 20.2646042,6.08049026 19.9337359,5.9249446 L19.8369274,5.87037881 L18.001,4.65071759 L16.1650884,5.87037881 L16.0682799,5.9249446 C15.7374116,6.08049026 15.3337451,5.97443402 15.1253388,5.66080596 C14.9169324,5.3471779 14.975531,4.93394581 15.2471044,4.68916802 L15.3349116,4.62105637 L17.5859195,3.12525804 L17.6834824,3.07042232 Z" />
  </svg>
);

FontSizeUpIcon.propTypes = iconPropTypes;

/**
 * FontSizeDownIcon - "A" with downward arrow (decrease font size)
 */
export const FontSizeDownIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <path d="M12.7975385,7.39384615 L12.8658462,7.49846154 L12.9230769,7.61538462 L16.6730769,16.6153846 C16.8854941,17.1251859 16.6444167,17.7106597 16.1346154,17.9230769 C15.6612285,18.1203215 15.1225933,17.9265426 14.8779876,17.4896781 L14.8269231,17.3846154 L13.833,15 L10.166,15 L9.17307692,17.3846154 C8.97583237,17.8580023 8.45692169,18.0996835 7.97562247,17.961721 L7.86538462,17.9230769 C7.39199769,17.7258324 7.15031654,17.2069217 7.28827901,16.7256225 L7.32692308,16.6153846 L11.0769231,7.61538462 C11.3846154,6.87692308 12.3569231,6.80307692 12.7975385,7.39384615 Z M12,10.6 L11,13 L13,13 L12,10.6 Z M20.876677,3.33675137 C21.0850834,3.65037943 21.0264848,4.06361153 20.7549114,4.30838932 L20.6671042,4.37650097 L18.4160963,5.8722993 C18.1960388,6.01852788 17.9180282,6.03680645 17.6834824,5.92713502 L17.5859195,5.8722993 L15.3349116,4.37650097 C14.9899208,4.14725399 14.8960918,3.68174224 15.1253388,3.33675137 C15.3337451,3.02312331 15.7374116,2.91706708 16.0682799,3.07261274 L16.1650884,3.12717853 L18.001,4.346 L19.8369274,3.12717853 C20.1819183,2.89793155 20.6474301,2.99176051 20.876677,3.33675137 Z" />
  </svg>
);

FontSizeDownIcon.propTypes = iconPropTypes;

/**
 * SettingsDotsIcon - Three vertical dots for settings menu
 */
export const SettingsDotsIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)}
    viewBox="0 0 24 24" 
    fill="currentColor"
    {...props}
  >
    <circle cx="12" cy="5" r="1.8" />
    <circle cx="12" cy="12" r="1.8" />
    <circle cx="12" cy="19" r="1.8" />
  </svg>
);

SettingsDotsIcon.propTypes = iconPropTypes;

/**
 * ShuffleIcon - Simple shuffle icon for randomize/regenerate actions
 */
export const ShuffleIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    {/* Left-to-right crossing paths */}
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h4l10 10h4" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 17h4l3-3" />
    {/* Right arrow (top) */}
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 7h4m0 0-2-2m2 2-2 2" />
    {/* Right arrow (bottom) */}
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h4m0 0-2-2m2 2-2 2" />
  </svg>
);

ShuffleIcon.propTypes = iconPropTypes;

/**
 * PlayIcon - Triangle play button for preview/play actions
 */
export const PlayIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <path d="M8.25 5.73v12.54c0 .77.83 1.25 1.5.86l10.5-6.27a1 1 0 000-1.72L9.75 4.87a1 1 0 00-1.5.86z" />
  </svg>
);

PlayIcon.propTypes = iconPropTypes;

/**
 * CameraIcon - Camera icon for screenshot/photo actions
 */
export const CameraIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h1.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0111.07 4h1.86a2 2 0 011.664.89l.812 1.22A2 2 0 0017.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <circle cx="12" cy="13" r="3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

CameraIcon.propTypes = iconPropTypes;
